import { REVIEW_NEW_PER_DAY } from '@shared/srs'
import { useStudentDetail, type AdminWordTotals } from '@web/lib/admin'
import { SendPushCard } from './SendPushCard'

/** Where she is in the course, what she's weak at, and whether she's showing up. */
export function StudentView({ userId }: { userId: string }) {
  const { data, isPending } = useStudentDetail(userId)

  if (isPending || !data) return <p className="text-ink-500">Loading…</p>

  const mastery = data.mastery
  const masteredCount = mastery.mastered ?? 0
  const knownCount = Object.values(mastery).reduce((a, b) => a + b, 0)

  return (
    <div className="grid gap-5 md:grid-cols-2">
      <section className="rounded-card bg-white p-5 ring-1 ring-brand-900/5">
        <h2 className="font-display text-lg font-bold text-ink-900">Course progress</h2>
        <ul className="mt-3 space-y-2.5">
          {data.units.map((unit) => {
            const pct = unit.total === 0 ? 0 : Math.round((unit.done / unit.total) * 100)
            return (
              <li key={unit.id}>
                <div className="flex items-baseline justify-between text-sm">
                  <span className="font-medium text-ink-900">{unit.title}</span>
                  <span className="text-ink-500 tabular-nums">
                    {unit.done}/{unit.total}
                    {unit.avgScore !== null && <> · {unit.avgScore}%</>}
                  </span>
                </div>
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-sand-200">
                  <div
                    className="h-full rounded-full bg-brand-600"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </li>
            )
          })}
        </ul>
      </section>

      <section className="rounded-card bg-white p-5 ring-1 ring-brand-900/5">
        <h2 className="font-display text-lg font-bold text-ink-900">Weakest skills</h2>
        <p className="mt-1 text-xs text-ink-500">
          First attempts only — a retry after seeing the answer proves nothing.
        </p>
        {data.skills.length === 0 ? (
          <p className="mt-3 text-sm text-ink-500">Not enough attempts yet.</p>
        ) : (
          <ul className="mt-3 space-y-2.5">
            {data.skills.slice(0, 8).map((skill) => (
              <li key={skill.tag}>
                <div className="flex items-baseline justify-between text-sm">
                  <span className="font-medium text-ink-900">{skill.label}</span>
                  <span className="text-ink-500 tabular-nums">
                    {skill.accuracyPct}% · {skill.attempts}
                  </span>
                </div>
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-sand-200">
                  <div
                    className={`h-full rounded-full ${
                      skill.accuracyPct < 50
                        ? 'bg-clay-500'
                        : skill.accuracyPct < 75
                          ? 'bg-gold-500'
                          : 'bg-brand-500'
                    }`}
                    style={{ width: `${skill.accuracyPct}%` }}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-card bg-white p-5 ring-1 ring-brand-900/5">
        <h2 className="font-display text-lg font-bold text-ink-900">Vocabulary</h2>
        <p className="mt-1 text-sm text-ink-700">
          {masteredCount} mastered of {knownCount} met
        </p>
        <ul className="mt-3 space-y-1.5 text-sm">
          {(['new', 'learning', 'familiar', 'strong', 'mastered'] as const).map((level) => (
            <li key={level} className="flex justify-between">
              {/*
                "New" is the bucket that tells you whether teaching has run
                ahead of review, so it says what it means rather than making
                you know that already.
              */}
              <span className="text-ink-700 capitalize">
                {level}
                {level === 'new' && (
                  <span className="ml-1.5 text-xs normal-case text-ink-400">
                    never reviewed
                  </span>
                )}
              </span>
              <span className="text-ink-500 tabular-nums">{mastery[level] ?? 0}</span>
            </li>
          ))}
        </ul>

        <ReviewBacklog words={data.words} />
      </section>

      <section className="rounded-card bg-white p-5 ring-1 ring-brand-900/5">
        <h2 className="font-display text-lg font-bold text-ink-900">Recent days</h2>
        {data.activity.length === 0 ? (
          <p className="mt-3 text-sm text-ink-500">No activity recorded yet.</p>
        ) : (
          <ul className="mt-3 space-y-1.5 text-sm">
            {data.activity.slice(0, 10).map((day) => (
              <li key={day.local_date} className="flex justify-between">
                <span className="text-ink-700 tabular-nums">{day.local_date}</span>
                <span className="text-ink-500 tabular-nums">
                  {day.xp} XP · {day.lessons_completed} lessons · {day.reviews_done} reviews
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <SendPushCard
        userId={userId}
        studentName={data.student.displayName}
        devices={data.pushDevices}
      />
    </div>
  )
}

/**
 * Whether review is keeping pace with teaching.
 *
 * This is the number whose absence hid a real bug for weeks: finishing a
 * lesson enrols its vocabulary, and for a long time none of it was ever
 * rationed back to her, so words piled up taught-but-never-recalled while
 * every visible figure looked healthy. A backlog is only alarming once
 * somebody can see it.
 */
function ReviewBacklog({ words }: { words: AdminWordTotals }) {
  const behind = words.newAvailable > 0

  return (
    <div className="mt-4 border-t border-sand-200 pt-3 text-sm">
      <dl className="space-y-1.5">
        <div className="flex justify-between">
          <dt className="text-ink-700">Due for review</dt>
          <dd className="text-ink-500 tabular-nums">{words.due}</dd>
        </div>
        <div className="flex justify-between">
          <dt className={behind ? 'font-medium text-gold-700' : 'text-ink-700'}>
            Taught, never recalled
          </dt>
          <dd
            className={`tabular-nums ${behind ? 'font-semibold text-gold-700' : 'text-ink-500'}`}
          >
            {words.newAvailable}
          </dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-ink-700">Met for the first time today</dt>
          <dd className="text-ink-500 tabular-nums">{words.introducedToday}</dd>
        </div>
      </dl>
      {behind && (
        <p className="mt-2 text-xs text-ink-500">
          Released into review at {REVIEW_NEW_PER_DAY} a day, so this clears in about{' '}
          {Math.ceil(words.newAvailable / REVIEW_NEW_PER_DAY)} days of practice.
        </p>
      )}
    </div>
  )
}
